import { mapValues } from 'lodash'

import { IndustryOrMaterial, TypeOfTesting } from '../logic/catalog'
import { LandingPageType, CollectionPageName } from '../logic/shared-types'
import { StaticPage } from '../logic/staticPage'

import { localeLanguage, localeValues, MeasurLocale } from './i18n'

type AllStaticPagePaths = {
  [page in StaticPage]: {
    [ml in MeasurLocale]: string
  }
}

export interface ISlugLink {
  locale: MeasurLocale
  slug: string
}

const staticPath = (path: string) =>
  Object.fromEntries(localeValues.map(ml => [ml, path])) as {
    [ml in MeasurLocale]: string
  }

export const testimonialPagePath = {
  [MeasurLocale.US_ENGLISH]: 'testimonials',
  [MeasurLocale.FINLAND_FINNISH]: 'referenssit',
}

export const compliancePagePath = {
  [MeasurLocale.US_ENGLISH]: 'compliance-and-quality',
  [MeasurLocale.FINLAND_FINNISH]: 'laatu-ja-vaatimustenmukaisuus',
}

export const staticPagePathComponents: AllStaticPagePaths = {
  [StaticPage.ABOUT]: {
    [MeasurLocale.US_ENGLISH]: 'about',
    [MeasurLocale.FINLAND_FINNISH]: 'meista',
  },
  [StaticPage.INDEX]: staticPath(''),
  [StaticPage.NOT_FOUND]: staticPath('404'),
  [StaticPage.CART]: {
    [MeasurLocale.US_ENGLISH]: 'cart',
    [MeasurLocale.FINLAND_FINNISH]: 'ostoskori',
  },
  [StaticPage.CART_BILLING]: {
    [MeasurLocale.US_ENGLISH]: 'cart/billing',
    [MeasurLocale.FINLAND_FINNISH]: 'ostoskori/maksutiedot',
  },
  [StaticPage.CART_REVIEW]: {
    [MeasurLocale.US_ENGLISH]: 'cart/review',
    [MeasurLocale.FINLAND_FINNISH]: 'ostoskori/esikatselu',
  },
  [StaticPage.TEAM]: {
    [MeasurLocale.US_ENGLISH]: 'team',
    [MeasurLocale.FINLAND_FINNISH]: 'tiimi',
  },
  [StaticPage.PRODUCTS]: {
    [MeasurLocale.US_ENGLISH]: 'products',
    [MeasurLocale.FINLAND_FINNISH]: 'tuotteet',
  },
  [StaticPage.PARTNERS]: {
    [MeasurLocale.US_ENGLISH]: 'partners',
    [MeasurLocale.FINLAND_FINNISH]: 'kumppaniksi',
  },
  [StaticPage.METHODS]: {
    [MeasurLocale.US_ENGLISH]: 'methods',
    [MeasurLocale.FINLAND_FINNISH]: 'menetelmat',
  },
  [StaticPage.CONTACT]: {
    [MeasurLocale.US_ENGLISH]: 'contact',
    [MeasurLocale.FINLAND_FINNISH]: 'ota-yhteytta',
  },
  [StaticPage.CAREERS]: {
    [MeasurLocale.US_ENGLISH]: 'careers',
    [MeasurLocale.FINLAND_FINNISH]: 'tyopaikat',
  },
  [StaticPage.PRIVACY]: {
    [MeasurLocale.US_ENGLISH]: 'privacy',
    [MeasurLocale.FINLAND_FINNISH]: 'tietosuoja',
  },
  [StaticPage.TERMS_SALES]: {
    [MeasurLocale.US_ENGLISH]: 'terms',
    [MeasurLocale.FINLAND_FINNISH]: 'toimitusehdot',
  },
  [StaticPage.TERMS_ONLINE]: {
    [MeasurLocale.US_ENGLISH]: 'online-terms',
    [MeasurLocale.FINLAND_FINNISH]: 'kayttoehdot',
  },
  [StaticPage.SHIPPING_INSTRUCTIONS]: {
    [MeasurLocale.US_ENGLISH]: 'shipping',
    [MeasurLocale.FINLAND_FINNISH]: 'lahetysohjeet',
  },
  [StaticPage.SOLUTIONS]: {
    [MeasurLocale.US_ENGLISH]: 'solutions',
    [MeasurLocale.FINLAND_FINNISH]: 'ratkaisut',
  },
  [StaticPage.ORDER_CONFIRMATION]: {
    [MeasurLocale.US_ENGLISH]: 'cart/confirmation',
    [MeasurLocale.FINLAND_FINNISH]: 'ostoskori/tilausvahvistus',
  },
  [StaticPage.PORTAL]: {
    [MeasurLocale.US_ENGLISH]: 'portal',
    [MeasurLocale.FINLAND_FINNISH]: 'portaali',
  },
  [StaticPage.PORTAL_ORDER_SUBMISSION_CONTACT_DETAILS]: {
    [MeasurLocale.US_ENGLISH]: 'portal/order/[id]/contact-details',
    [MeasurLocale.FINLAND_FINNISH]: 'portaali/tilaus/[id]/yhteystiedot',
  },
  [StaticPage.PORTAL_ORDER_SUBMISSION_CONFIRMATION]: {
    [MeasurLocale.US_ENGLISH]: 'portal/order/[id]/confirmation',
    [MeasurLocale.FINLAND_FINNISH]: 'portaali/tilaus/[id]/vahvistus',
  },
  [StaticPage.PORTAL_ORDER_SUBMISSION_SAMPLES]: {
    [MeasurLocale.US_ENGLISH]: 'portal/order/[id]/samples',
    [MeasurLocale.FINLAND_FINNISH]: 'portaali/tilaus/[id]/naytteet',
  },
  [StaticPage.PORTAL_ORDER_SUBMISSION_REVIEW]: {
    [MeasurLocale.US_ENGLISH]: 'portal/order/[id]/review',
    [MeasurLocale.FINLAND_FINNISH]: 'portaali/tilaus/[id]/esikatselu',
  },
  [StaticPage.PORTAL_ORDERS_ID]: {
    [MeasurLocale.US_ENGLISH]: 'portal/orders/[id]',
    [MeasurLocale.FINLAND_FINNISH]: 'portaali/tilaukset/[id]',
  },
  [StaticPage.PORTAL_OFFERS_ID]: {
    [MeasurLocale.US_ENGLISH]: 'portal/offers/[id]',
    [MeasurLocale.FINLAND_FINNISH]: 'portaali/tarjoukset/[id]',
  },
  [StaticPage.BLOG]: staticPath('blog'),
}

export const defaultLocale = MeasurLocale.US_ENGLISH

export const localePrefix = (locale: MeasurLocale) =>
  locale === defaultLocale ? '' : `/${localeLanguage(locale)}`

export const staticPagePaths: AllStaticPagePaths = mapValues(
  staticPagePathComponents,
  localeComponents =>
    mapValues(
      localeComponents,
      (component, locale: MeasurLocale) =>
        `${localePrefix(locale)}/${component ? component + '/' : ''}`,
    ),
) as unknown as AllStaticPagePaths

export const staticPageSlugs: (page: StaticPage) => ISlugLink[] = page =>
  Object.entries(staticPagePaths[page]).map(([slugLocale, slug]) => ({
    locale: slugLocale as MeasurLocale,
    slug,
  }))

export const landingUrl = (
  locale: MeasurLocale,
  pageSlug: string,
  pageType: LandingPageType,
) => {
  if (pageType === 'solutions') {
    return (
      [
        localePrefix(locale),
        staticPagePathComponents[StaticPage.SOLUTIONS][locale],
        pageSlug,
      ].join('/') + '/'
    )
  }
  return [localePrefix(locale), pageSlug].join('/') + '/'
}

export const categoryLandingUrl = (
  locale: MeasurLocale,
  pageSlug: IndustryOrMaterial | TypeOfTesting,
) =>
  [
    localePrefix(locale),
    staticPagePathComponents[StaticPage.PRODUCTS][locale],
    'c',
    pageSlug,
  ].join('/') + '/'

export const categoryLandingSlugs = (
  category: IndustryOrMaterial | TypeOfTesting,
) =>
  Object.entries(staticPagePaths[StaticPage.PRODUCTS]).map(([slugLocale]) => ({
    locale: slugLocale as MeasurLocale,
    slug: categoryLandingUrl(slugLocale as MeasurLocale, category),
  }))

export const collectionUrl = (
  locale: MeasurLocale,
  collectionPageName: CollectionPageName,
) => {
  if (collectionPageName === 'Methods page') {
    return (
      [
        localePrefix(locale),
        staticPagePathComponents[StaticPage.METHODS][locale],
      ].join('/') + '/'
    )
  }
  if (collectionPageName === 'Solutions page') {
    return (
      [
        localePrefix(locale),
        staticPagePathComponents[StaticPage.SOLUTIONS][locale],
      ].join('/') + '/'
    )
  }
  throw new Error('Unknown static page name')
}

export const methodUrl = (locale: MeasurLocale, pageSlug: string) =>
  [
    localePrefix(locale),
    staticPagePathComponents[StaticPage.METHODS][locale],
    pageSlug,
  ].join('/') + '/'

export const teamMemberUrl = (locale: MeasurLocale, pageSlug: string) =>
  [
    localePrefix(locale),
    staticPagePathComponents[StaticPage.TEAM][locale],
    pageSlug,
  ].join('/') + '/'

export const productUrl = (locale: MeasurLocale, pageSlug: string) =>
  [
    localePrefix(locale),
    staticPagePathComponents[StaticPage.PRODUCTS][locale],
    pageSlug,
  ].join('/') + '/'

export const blogPageUrl = (locale: MeasurLocale, pageNumber: number) =>
  [
    localePrefix(locale),
    staticPagePathComponents[StaticPage.BLOG][locale],
    pageNumber === 1 ? '' : `page/${pageNumber}`,
  ].join('/')

export const blogPostUrl = (locale: MeasurLocale, pageSlug: string) =>
  [
    localePrefix(locale),
    staticPagePathComponents[StaticPage.BLOG][locale],
    pageSlug,
  ].join('/') + '/'
